import {Inject, Injectable, Optional} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {DOCUMENT} from '@angular/common';
import { REQUEST } from '../providers/http-context-providers';
import type { Request } from 'express';

type LocaleCode = 'en-US' | 'pl-PL' | 'de-DE';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private static readonly DEFAULT_HOST = 'watchthisgarage.com';
  private static readonly LOCALE_HOSTS: Record<LocaleCode, string> = {
    'en-US': 'en-us.watchthisgarage.com',
    'pl-PL': 'pl-pl.watchthisgarage.com',
    'de-DE': 'de-de.watchthisgarage.com',
  };

  constructor(
    private readonly meta: Meta,
    private readonly title: Title,
    @Inject(DOCUMENT) private readonly document: Document,
    @Optional() @Inject(REQUEST) private readonly request: Request | null = null
  ) {}

  setTitle(title: string, suffix: string = ' | WTG | Watch This Garage'): void {
    this.title.setTitle(`${title}${suffix}`);
  }

  setDescription(description: string): void {
    if (!description) {
      return;
    }
    this.meta.updateTag({name: 'description', content: description});
  }

  setCanonical(url: string): void {
    if (!url) {
      return;
    }
    let link = this.document.querySelector("link[rel='canonical']") as HTMLLinkElement;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', this.normalizeAbsoluteUrl(url, false));
  }

  setOpenGraph(data: {title?: string; description?: string; url?: string; image?: string; type?: string}): void {
    const {title, description, url, image, type} = data;
    if (title) this.meta.updateTag({property: 'og:title', content: title});
    if (description) this.meta.updateTag({property: 'og:description', content: description});
    if (url) this.meta.updateTag({property: 'og:url', content: this.normalizeAbsoluteUrl(url, false)});
    if (image) this.meta.updateTag({property: 'og:image', content: image});
    this.meta.updateTag({property: 'og:type', content: type || 'website'});
  }

  setJsonLd(schema: object, id: string = 'seo-jsonld'): void {
    if (!schema) {
      return;
    }
    const existing = this.document.getElementById(id);
    if (existing) {
      existing.remove();
    }
    const script = this.document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('id', id);
    script.textContent = JSON.stringify(schema);
    this.document.head.appendChild(script);
  }

  setRobots(content: string): void {
    if (!content) {
      return;
    }

    this.meta.updateTag({name: 'robots', content});
    this.meta.updateTag({name: 'googlebot', content});
  }

  clearRobots(): void {
    this.meta.removeTag("name='robots'");
    this.meta.removeTag("name='googlebot'");
  }

  setIndexable(indexable: boolean, follow: boolean = true): void {
    if (indexable) {
      this.clearRobots();
      return;
    }

    this.setRobots(follow ? 'noindex, follow' : 'noindex, nofollow, noarchive, nosnippet');
  }

  setHtmlLang(language: string | null | undefined): void {
    const normalizedLanguage = this.normalizeLanguageCode(language);
    this.document.documentElement?.setAttribute('lang', normalizedLanguage);
  }

  setLocaleAlternates(pathOrUrl: string): void {
    const normalizedPath = this.normalizePath(pathOrUrl);
    this.removeManagedAlternateLinks();

    const alternateEntries: Array<{hreflang: string; href: string}> = [
      { hreflang: 'en-us', href: `${this.getOriginForLocale('en-US')}${normalizedPath}` },
      { hreflang: 'pl-pl', href: `${this.getOriginForLocale('pl-PL')}${normalizedPath}` },
      { hreflang: 'de-de', href: `${this.getOriginForLocale('de-DE')}${normalizedPath}` },
      { hreflang: 'x-default', href: `${this.getDefaultOrigin()}${normalizedPath}` },
    ];

    alternateEntries.forEach(({ hreflang, href }) => {
      const link = this.document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', href);
      link.setAttribute('data-seo-locale-alternate', 'true');
      this.document.head.appendChild(link);
    });
  }

  getAbsoluteUrl(pathOrUrl: string): string {
    return this.normalizeAbsoluteUrl(pathOrUrl, true);
  }

  private removeManagedAlternateLinks(): void {
    this.document.querySelectorAll("link[rel='alternate'][hreflang]").forEach((node) => node.remove());
  }

  private normalizeAbsoluteUrl(pathOrUrl: string, keepQuery: boolean): string {
    const currentOrigin = this.getCanonicalOrigin();
    const resolvedUrl = this.parseUrl(pathOrUrl);
    if (!resolvedUrl) {
      return `${currentOrigin}${this.normalizePath(pathOrUrl)}`;
    }

    const normalizedHost = this.normalizeHost(resolvedUrl.hostname);
    const finalOrigin = this.belongsToWatchThisGarage(normalizedHost)
      ? `${resolvedUrl.protocol}//${this.normalizeHostForCanonical(normalizedHost)}`
      : currentOrigin;
    const normalizedPath = this.normalizePath(resolvedUrl.pathname);
    const search = keepQuery ? this.getFilteredSearch(resolvedUrl.searchParams) : '';
    return `${finalOrigin}${normalizedPath}${search}`;
  }

  private parseUrl(pathOrUrl: string): URL | null {
    try {
      return new URL(pathOrUrl, this.getCanonicalOrigin());
    } catch {
      return null;
    }
  }

  private normalizePath(pathOrUrl: string): string {
    const resolvedUrl = this.parseUrl(pathOrUrl);
    if (!resolvedUrl) {
      return pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    }

    const search = this.getFilteredSearch(resolvedUrl.searchParams);
    return `${resolvedUrl.pathname || '/'}${search}`;
  }

  private getFilteredSearch(searchParams: URLSearchParams): string {
    const filteredParams = new URLSearchParams(searchParams);
    filteredParams.delete('lang');
    const queryString = filteredParams.toString();

    return queryString ? `?${queryString}` : '';
  }

  private getCanonicalOrigin(): string {
    return `${this.getRequestProtocol()}://${this.normalizeHostForCanonical(this.getCurrentHost())}`;
  }

  private getOriginForLocale(locale: LocaleCode): string {
    return `${this.getRequestProtocol()}://${SeoService.LOCALE_HOSTS[locale]}`;
  }

  private getDefaultOrigin(): string {
    return `${this.getRequestProtocol()}://${SeoService.DEFAULT_HOST}`;
  }

  private getCurrentHost(): string {
    const requestHost = this.readHeaderValue(this.request?.headers?.['x-forwarded-host'])
      || this.readHeaderValue(this.request?.headers?.['x-original-host'])
      || this.readHeaderValue(this.request?.headers?.host);
    const documentHost = this.document?.location?.host || this.document?.location?.hostname;

    return this.normalizeHost(requestHost || documentHost || SeoService.DEFAULT_HOST);
  }

  private getRequestProtocol(): string {
    const forwardedProto = this.readHeaderValue(this.request?.headers?.['x-forwarded-proto']);
    const documentProtocol = this.document?.location?.protocol?.replace(/:$/, '');

    return forwardedProto || documentProtocol || 'https';
  }

  private normalizeHost(host: string): string {
    return host.split(':')[0]?.trim().toLowerCase() || SeoService.DEFAULT_HOST;
  }

  private normalizeHostForCanonical(host: string): string {
    return this.normalizeHost(host);
  }

  private belongsToWatchThisGarage(host: string): boolean {
    return host === SeoService.DEFAULT_HOST || host.endsWith('.watchthisgarage.com');
  }

  private normalizeLanguageCode(language: string | null | undefined): string {
    const lowerLanguage = (language || 'en-US').toLowerCase();

    switch (lowerLanguage) {
      case 'pl':
      case 'pl-pl':
        return 'pl-PL';
      case 'de':
      case 'de-de':
        return 'de-DE';
      default:
        return 'en-US';
    }
  }

  private readHeaderValue(header: string | string[] | undefined): string | undefined {
    if (Array.isArray(header)) {
      return header[0];
    }

    if (typeof header !== 'string') {
      return undefined;
    }

    return header.split(',')[0]?.trim() || undefined;
  }
}
